<template>
  <NumeraiModal
    ref="modal"
    :dialog="dialog"
    :button="false"
    :width="600"
    :show-footer="false"
    @close="onModalClose"
  >
    <template v-slot:header>
      <div class="pt-1 pl-2 mb-4 flexrow space_between">
        <h2 class="title">
          Model Upload
        </h2>
        <button @click="onModalClose">
          <v-icon>mdi-close</v-icon>
        </button>
      </div>
    </template>
    <v-container>
      <div class="mb-4">
        <div class="flexrow space_between">
          <p class="mb-2">Model Name</p>
          <p class="mb-2">{{ modelName }}</p>
        </div>
        <template v-if="pickle">
          <div class="flexrow space_between">
            <p class="mb-2">Filename</p>
            <p class="mb-2">
              {{ trimFilenameHash(pickle.filename) }}
            </p>
          </div>
          <div class="flexrow space_between">
            <p class="mb-2">Dataset Version</p>
            <p class="mb-2">
              {{ pickle.dataVersion }}
            </p>
          </div>
          <div class="flexrow space_between">
            <p class="mb-2">Python Version</p>
            <p class="mb-2">{{ pickle.dockerImageName }}</p>
          </div>
          <div class="flexrow space_between">
            <p class="mb-2">Uploaded</p>
            <p class="mb-2">
              {{ moment(pickle.insertedAt).format('l LT') }}
            </p>
          </div>
        </template>
      </div>
      <div v-if="!pickle" class="info-box">
        <p>
          Upload your model to automatically generate live predictions for every
          daily round.
        </p>
        <div class="flexrow space_around">
          <a :href="exampleNotebookLink" rel="noopener" target="_blank"
            ><p>Example Notebook</p></a
          >
          <a
            href="https://docs.numer.ai/numerai-tournament/submissions/model-upload"
            rel="noopener"
            target="_blank"
            ><p>Documentation</p></a
          >
        </div>
      </div>
      <v-container v-if="!isValidating" class="pa-0">
        <v-container class="tabs pb-0">
          <v-row>
            <v-col
              :class="{
                selectedTab: selectedTab === 'upload',
                disabledTab: selectedTab !== 'upload' && inProgress,
              }"
              @click="selectTab('upload')"
            >
              New Upload
            </v-col>
            <v-col
              :class="{
                selectedTab: selectedTab === 'advanced',
                disabledTab:
                  (selectedTab !== 'advanced' && inProgress) || disableAdvanced,
              }"
              @click="selectTab('advanced')"
            >
              Settings
            </v-col>
          </v-row>
        </v-container>
        <v-container
          v-if="selectedTab == 'upload'"
          class="tabContent roundedBottom roundedTopRight pt-6 pa-8"
        >
          <label>Select Pickle File </label>
          <label
            for="file-upload"
            class="file-input mb-6 cursor-pointer"
            :class="{ disabled: inProgress }"
          >
            <input
              id="file-upload"
              ref="fileUpload"
              type="file"
              :disabled="inProgress"
              @change="onFileChange"
            />
            {{
              selectedFile ? selectedFile.name : '-- Select a file (.pkl) --'
            }}
          </label>
          <label
            >Select Python Version
            <NumeraiTooltip
              text="Select the Python version used to create your pickle"
          /></label>
          <div class="select-dropdown">
            <select
              class="select-input mb-6"
              :disabled="inProgress"
              @change="onDockerImageChange"
            >
              <template v-for="option in dockerImageList">
                <option
                  :key="option.value"
                  :value="option.value"
                  :selected="selectedDockerImageId === option.value"
                  >{{ option.text }}</option
                >
              </template>
            </select>
          </div>
          <label
            >Dataset Version
            <NumeraiTooltip
              text="New uploads may only use the latest dataset version"
            />
          </label>
          <div :class="{ 'select-dropdown': dataVersionList.length > 1 }">
            <select
              class="select-input mb-8"
              :disabled="inProgress"
              @change="onDataVersionChange"
            >
              <template v-for="option in dataVersionList">
                <option
                  :key="option.value"
                  :value="option.value"
                  :selected="
                    dataVersionList.length === 1
                      ? true
                      : selectedDataVersionId === option.value
                  "
                  >{{ option.text }}</option
                >
              </template>
            </select>
          </div>
          <div class="flexrow justify_end">
            <button
              class="numerai-button"
              :class="{ disabled: isUploadDisabled }"
              @click="onUpload"
            >
              <div v-if="isUploading" class="loading-spinner">
                <v-icon color="#ffffff">mdi-cached</v-icon>
              </div>
              <span v-else>Upload{{ pickle ? ' and Overwrite' : '' }}</span>
            </button>
          </div>
        </v-container>
        <v-container
          v-if="selectedTab == 'advanced'"
          class="tabContent roundedBottom roundedTopLeft pa-8"
        >
          <template>
            <label
              >Disable model and clear configuration
              <NumeraiTooltip
                text="Your uploaded model will no longer be used to generate submissions"
            /></label>
            <div class="flexrow justify_end pt-6">
              <button
                class="numerai-button button-danger"
                :class="{
                  disabled: inProgress || !pickle,
                }"
                @click="disable"
              >
                <div v-if="isDisabling" class="loading-spinner">
                  <v-icon color="#ffffff">mdi-cached</v-icon>
                </div>
                <span v-else>Disable</span>
              </button>
            </div>
          </template>
        </v-container>
      </v-container>
      <v-container v-else class="flexrow align_center justify_center mb-4">
        <PickleStatus
          :hide-context="true"
          :hide-logs="true"
          :pickle-id="pickle.id"
        />
      </v-container>
    </v-container>
  </NumeraiModal>
</template>

<script>
import { mapActions } from 'vuex'
import { trimFilenameHash } from '@/lib/utils'
import moment from 'moment'

export default {
  components: {
    NumeraiModal: () => import('@/components/shared/NumeraiModal'),
    PickleStatus: () => import('@/components/shared/PickleStatus'),
    NumeraiTooltip: () => import('@/components/shared/NumeraiTooltip'),
  },
  props: {
    model: { type: Object, default: null },
    pickle: { type: Object, default: null },
    pickles: { type: Array, default: () => [] },
    dialog: { type: Boolean, default: false },
    dockerImages: { type: Array, default: () => [] },
    dataVersions: { type: Array, default: () => [] },
    isValidating: { type: Boolean, default: false },
    onClose: { type: Function, default: null },
    onUpdateSuccess: { type: Function, default: () => {} },
    onDisable: { type: Function, default: () => {} },
  },
  data() {
    return {
      selectedTab: 'upload',
      selectedFile: null,
      selectedPickleId: null,
      selectedDataVersionId: null,
      selectedDockerImageId: null,
      isUploading: false,
      isUpdating: false,
      isDisabling: false,
    }
  },
  computed: {
    exampleNotebookLink() {
      // eslint-disable-next-line max-len
      return 'https://colab.research.google.com/github/numerai/example-scripts/blob/master/hello_numerai.ipynb'
    },
    modelName() {
      return this.model && this.model.name && this.model.name.toUpperCase()
    },
    inProgress() {
      return (
        this.isUploading ||
        this.isUpdating ||
        this.isDisabling ||
        this.isValidating
      )
    },
    isUploadDisabled() {
      if (this.inProgress) {
        return true
      }

      if (
        !this.selectedFile ||
        !this.selectedDataVersionId ||
        !this.selectedDockerImageId
      ) {
        return true
      }

      return false
    },
    noPreviousPickles() {
      return this.pickleList.length === 0
    },
    disableAdvanced() {
      return this.noPreviousPickles && !this.pickle
    },
    pickleList() {
      if (this.model && this.model.id) {
        return this.pickles
          .filter(pickle => {
            if (this.pickle) {
              return (
                pickle.modelId === this.model.id &&
                pickle.validationStatus === 'validated' &&
                pickle.id !== this.pickle.id
              )
            } else {
              return (
                pickle.modelId === this.model.id &&
                pickle.validationStatus === 'validated'
              )
            }
          })
          .map(pickle => {
            return {
              text: `${trimFilenameHash(pickle.filename)} ${moment(
                pickle.insertedAt,
              ).format('l LT')}`,
              value: pickle.id,
            }
          })
      } else {
        return []
      }
    },
    dockerImageList() {
      return this.dockerImages
        .map(image => ({
          default: image.default,
          text:
            image.name +
            `${image.default ? ' (Google Colab)' : ''}${
              image.experimental ? ' 🧪' : ''
            }`,
          value: image.id,
        }))
        .sort(image => (image.default ? -1 : 1))
    },
    dataVersionList() {
      return this.dataVersions
        .map(version => {
          let text = version.name
          if (version.default) {
            text += ' (Latest)'
          }
          if (version.experimental) {
            text += ' 🧪'
          }
          return {
            default: version.default,
            text: text,
            value: version.id,
          }
        })
        .sort(version => (version.default ? -1 : 1))
    },
    shouldUpdatePickle() {
      return (
        (!this.pickle && this.selectedPickleId) ||
        this.pickle.id !== this.selectedPickleId
      )
    },
  },
  watch: {
    model(newVal) {
      if (!this.pickle && newVal && this.model && newVal.id !== this.model.id) {
        this.setDefaults()
      }
    },
    dockerImages(newVal) {
      if (newVal && !this.pickle) {
        this.setDefaultDockerImage(newVal)
      }
    },
    dataVersions(newVal) {
      if (newVal && !this.pickle) {
        this.setDefaultDataVersion(newVal)
        if (newVal.length === 1) {
          this.selectedDataVersionId = newVal[0].id
        }
      }
    },
  },
  methods: {
    ...mapActions(['fetchAccountInfo', 'showSnackBar', 'closeSnackBar']),
    trimFilenameHash,
    moment,
    selectTab(tab) {
      if ((tab === 'advanced' && this.disableAdvanced) || this.inProgress) {
        return
      }
      this.selectedTab = tab
    },
    setDefaults() {
      this.selectedTab = 'upload'
      this.isUpdating = false
      this.isUploading = false
      this.selectedFile = null
      this.selectedPickleId = null
      this.setDefaultDockerImage()
      this.setDefaultDataVersion()

      if (this.$refs.fileUpload) {
        this.$refs.fileUpload.value = ''
      }
    },
    setDefaultDockerImage(list) {
      const dockerImages = list || this.dockerImages
      this.selectedDockerImageId =
        dockerImages && dockerImages.length
          ? dockerImages.filter(image => image.default)[0].id
          : null
    },
    setDefaultDataVersion(versions) {
      const dataVersions = versions || this.dataVersions
      this.selectedDataVersionId =
        dataVersions && dataVersions.length
          ? dataVersions.filter(version => version.default)[0].id
          : null
    },
    onModalClose() {
      this.onClose()
      this.setDefaults()
    },
    onFileChange(event) {
      this.selectedFile = event.target.files[0]
    },
    async onUpload() {
      if (!this.selectedFile || this.isUploadDisabled) {
        return
      }

      try {
        this.isUploading = true
        this.showSnackBar({ msg: 'Authenticating...', time: 0 })
        const { url, filename } = await this.$client.getModelUploadAuth({
          filename: this.selectedFile.name,
          modelId: this.model.id,
        })
        this.showSnackBar({ msg: 'Uploading...', time: 0 })
        await fetch(url, {
          method: 'PUT',
          body: this.selectedFile,
        })
        await this.$client.createModelUpload({
          filename,
          modelId: this.model.id,
          dataVersionId: this.selectedDataVersionId,
          dockerImageId: this.selectedDockerImageId,
        })
        this.showSnackBar({ msg: 'Upload accepted' })
        this.$emit('upload')
        await this.fetchAccountInfo()
        this.onUploadSuccess()
      } catch (e) {
        this.showSnackBar({ msg: e.message, time: 0 })
      } finally {
        this.isUploading = false
      }
    },
    onPickleChange(event) {
      this.selectedPickleId = event.target.value
    },
    onDataVersionChange(event) {
      this.selectedDataVersionId = event.target.value
    },
    onDockerImageChange(event) {
      this.selectedDockerImageId = event.target.value
    },
    onUpdateClick() {
      if (this.inProgress || !this.selectedPickleId) {
        return
      } else {
        this.onUpdate()
      }
    },
    async onUpdate() {
      if (!this.shouldUpdatePickle) {
        return
      }

      try {
        this.isUpdating = !this.isDisabling
        await this.assignPickleToModel(this.selectedPickleId, this.model.id)
        await this.fetchAccountInfo()
        this.onUpdateSuccess(this.model)
        this.onModalClose()
      } catch (e) {
        this.showSnackBar({ msg: e.message })
      } finally {
        this.isUpdating = false
      }
    },
    async disable() {
      if (this.inProgress) {
        return
      }
      this.isDisabling = true
      this.selectedPickleId = null
      await this.onUpdate()
      this.isDisabling = false
      this.onDisable()
    },
    async assignPickleToModel(pickleId, modelId) {
      const res = await this.$client.assignPickleToModel({ pickleId, modelId })
      return res
    },
    onUploadSuccess() {
      this.isUploading = false
      this.closeSnackBar()
      this.onModalClose()
      this.onUpdateSuccess(this.model)
    },
  },
}
</script>

<style scoped>
.disabledTab {
  color: var(--color-grey30);
}

.disabledTab:hover {
  cursor: not-allowed;
}

.numerai-button {
  width: 200px;
}

.numerai-button.uploading:hover {
  cursor: progress;
}

.loading-spinner {
  animation: loader 1s infinite linear;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

.numerai-button.disabled {
  filter: brightness(0.5);
}

.numerai-button.disabled:hover {
  cursor: not-allowed;
}

.info-box {
  padding: 20px 30px;
  border-radius: 3px;
  background-color: var(--paper-background-highlight2);
  margin-bottom: 20px;
}

.tabs {
  text-align: center;
  cursor: pointer;
  color: var(--color-secondary);
  border-radius: 3px;
  font-size: 16px;
  font-weight: bold;
}

.selectedTab {
  background-color: var(--paper-background-highlight1);
  border-radius: 3px 3px 0px 0px;
}

.tabContent {
  background-color: var(--paper-background-highlight1);
  padding: 15px;
}

.roundedBottom {
  border-radius: 0 0 3px 3px;
}

.roundedTopLeft {
  border-top-left-radius: 3px;
}

.roundedTopRight {
  border-top-right-radius: 3px;
}

label,
input {
  font-size: 16px;
}

.select-input,
.file-input,
input {
  height: 34px;
  font-size: 16px;
  display: block;
  width: 100%;
  background: white;
  color: var(--color-dark-grey);
  border-radius: 3px;
  padding: 5px 15px;
  margin-top: 6px;
}

.file-input {
  padding-left: 19px;
}

input[type='file'] {
  display: none;
}

.file-input.disabled {
  filter: brightness(0.98);
}

.file-input.disabled:hover {
  cursor: not-allowed;
}

.file-input:hover {
  cursor: text;
}

select {
  appearance: none;
}

option {
  background: #ffffff;
}

select:hover {
  cursor: pointer;
}

.select-dropdown {
  background-color: #ffffff;
  position: relative;
}

.select-dropdown:after {
  content: ' ';
  position: absolute;
  top: 50%;
  margin-top: -2px;
  right: 10px;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid var(--color-almost-black);
}

.cursor-pointer:hover {
  cursor: pointer;
}

select:disabled,
.select-dropdown.disabled:hover {
  cursor: not-allowed;
}

.select-dropdown .v-icon {
  position: absolute;
  top: 4px;
  right: 5px;
}
</style>
